import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Node, mergeAttributes } from '@tiptap/core';

// Mirror the custom Video node from the editor
const VideoNode = Node.create({
  name: 'videoNode',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      caption: { default: '' },
    };
  },
  parseHTML() { return [{ tag: 'div[data-video]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-video': true }, HTMLAttributes)];
  },
  addNodeView() {
    return ({ node }) => {
      const wrap = document.createElement('figure');
      wrap.style.cssText = 'margin:2em 0;';
      const vid = document.createElement('video');
      vid.src = node.attrs.src;
      vid.controls = true;
      vid.style.cssText = 'width:100%;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,.15);';
      wrap.appendChild(vid);
      if (node.attrs.caption) {
        const cap = document.createElement('figcaption');
        cap.textContent = node.attrs.caption;
        cap.style.cssText = 'text-align:center;font-size:13px;color:#94a3b8;margin-top:8px;font-style:italic;';
        wrap.appendChild(cap);
      }
      return { dom: wrap };
    };
  },
});

export default function ContentRenderer({ content }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: 'code-block' } },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ImageExtension.configure({ HTMLAttributes: { class: 'u-content-img' } }),
      LinkExtension.configure({ openOnClick: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      Youtube.configure({ width: '100%', height: 400 }),
      VideoNode,
    ],
    content: content || '',
    editable: false,
    editorProps: {
      attributes: { class: 'u-content' },
    },
  });

  if (!content) {
    return (
      <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
        No content available.
      </div>
    );
  }

  return (
    <>
      <EditorContent editor={editor} />
      <style>{`
        .u-content iframe { width: 100%; border-radius: 12px; margin: 2em 0; border: none; }
        .u-content .youtube-embed { border-radius: 12px; overflow: hidden; margin: 2em 0; }
        .u-content mark { background: #fef08a; border-radius: 3px; padding: 1px 2px; }
        .code-block {
          background: #1e293b; color: #e2e8f0; padding: 24px; border-radius: 12px;
          margin: 2em 0; font-family: 'JetBrains Mono','Courier New',monospace;
          font-size: .875em; overflow-x: auto; line-height: 1.7;
        }
      `}</style>
    </>
  );
}
